import React from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export type TrqPosition = 'within' | 'over';

export type TrqPanelVariant = {
  label: string;
  rateText: string;
  rateDisplay: string;
};

export type TrqPanelSide = {
  versionType: string | null;
  rateNarrative: string | null;
  rateVaries: boolean;
  permitRequirement: string | null;
  rateDisplay: string | null;
  // Line-level rates recovered from the narrative. When present the user picks
  // which describes their goods, and that choice supplies the rate.
  variants: TrqPanelVariant[];
};

export type TrqPanelQuota = {
  iclItem: string | null;
  productName: string | null;
  appliesTo: string[];
  quotaYearFrom: string | null;
  quotaYearTo: string | null;
  administrationMethod: string | null;
  sharedWith: string | null;
  within: TrqPanelSide | null;
  over: TrqPanelSide | null;
  missingSides: Array<'within' | 'over'>;
};

type Props = {
  quotas: TrqPanelQuota[];
  positions: Record<string, TrqPosition>;
  variantChoice?: Record<string, number>;
  onDeclare?: (iclItem: string, position: TrqPosition) => void;
  onChooseVariant?: (
    iclItem: string,
    side: TrqPosition,
    index: number,
  ) => void;
  onViewDetails?: () => void;
  disabled?: boolean;
  // Takes two grid columns when a neighbouring card has nothing to show.
  wide?: boolean;
};

// The quota-position declaration, and the two rates it chooses between.
//
// The declaration is the most consequential input on the page: within-access
// duty on supply-managed goods is nominal (0.86¢ per chick) while over-access
// is deliberately prohibitive (259% on dairy). Showing both rates against each
// other is the point — a user who cannot see the gap has no way to know how
// much rides on getting the declaration right.
const CanadaTrqPanel: React.FC<Props> = ({
  quotas,
  positions,
  variantChoice = {},
  onDeclare,
  onChooseVariant,
  onViewDetails,
  disabled = false,
  wide = false,
}) => {
  // The card holds its slot even with nothing to show. On the Canada tab it
  // replaces the Exclusion Applicability Assistant outright — that card is
  // driven by US Section 232 exclusion codes and can never have anything to
  // say here — so returning null would leave a gap in the row.
  if (!quotas || quotas.length === 0) {
    return (
      <div className='tariff-tool-insights-card'>
        <div className='tariff-tool-insights-card-header'>
          <div className='screening-ai-label-text'>Tariff-Rate Quota</div>
        </div>
        <div className='tariff-desc-text'>
          No tariff-rate quota applies to this HS code and origin.
        </div>
      </div>
    );
  }

  return (
    <div
      className={`tariff-tool-insights-card trq-card${
        wide ? ' trq-card-wide' : ''
      }`}
    >
      <div className='tariff-tool-insights-card-header'>
        <div className='screening-ai-label-text'>Tariff-Rate Quota</div>
      </div>

      {quotas.map((quota) => {
        const key = quota.iclItem || quota.productName || 'quota';
        const declared = positions[quota.iclItem || ''];

        const sides: Array<{ id: TrqPosition; label: string; side: TrqPanelSide | null }> = [
          { id: 'within', label: 'Within access', side: quota.within },
          { id: 'over', label: 'Over access', side: quota.over },
        ];

        return (
          <div key={key} className='trq-quota'>
            <div className='trq-quota-product'>{quota.productName}</div>
            <div className='trq-quota-meta'>
              {quota.iclItem ? <span>{quota.iclItem}</span> : null}
              {quota.administrationMethod ? (
                <span>{quota.administrationMethod}</span>
              ) : null}
              {quota.quotaYearFrom && quota.quotaYearTo ? (
                <span>
                  Quota year {quota.quotaYearFrom} – {quota.quotaYearTo}
                </span>
              ) : null}
            </div>

            {/* The quota is often pooled across several ICL items, so the
                importer's volume counts against capacity they do not solely
                control. Worth saying plainly. */}
            {quota.sharedWith ? (
              <div className='trq-quota-shared'>
                Shared quota — {quota.sharedWith}
              </div>
            ) : null}

            <div className='trq-declare-prompt'>
              Is this shipment within or over the quota?
            </div>

            <div className='trq-options'>
              {sides.map(({ id, label, side }) => {
                // Where the group's rate differs by line, the narrative
                // sometimes names each one; those become the choice, and the
                // side itself is declared by picking one.
                const variants = side?.variants || [];
                // A line is only "chosen" on the side that is actually
                // declared. Without this the other side keeps its highlight
                // and both cards look selected at once.
                const chosenIndex =
                  declared === id
                    ? variantChoice[`${quota.iclItem || ''}|${id}`]
                    : undefined;
                // A side with no record, or one whose rate cannot price this
                // line and offers no variants to pick from, is not declarable:
                // selecting it would clear the pending state without there
                // being a rate to calculate from.
                const unavailable =
                  !side || (!side.rateDisplay && variants.length === 0);
                const isSelected = declared === id;
                return (
                  <button
                    type='button'
                    key={id}
                    className={`trq-option${isSelected ? ' is-active' : ''}${
                      unavailable ? ' is-unavailable' : ''
                    }`}
                    disabled={disabled || unavailable}
                    onClick={() =>
                      disabled || unavailable
                        ? undefined
                        : onDeclare?.(quota.iclItem || '', id)
                    }
                  >
                    <div className='trq-option-label'>{label}</div>
                    {/* Where lines are on offer, the chosen one IS the rate
                        — the group's stored figure describes a different
                        line, so showing it here would disagree with the duty
                        actually calculated. */}
                    <div className='trq-option-rate'>
                      {variants.length > 0
                        ? chosenIndex !== undefined
                          ? variants[chosenIndex]?.rateDisplay ||
                            'Rate unavailable'
                          : 'Varies by tariff line'
                        : side?.rateDisplay ||
                          (side ? 'Varies by tariff line' : 'Not in dataset')}
                    </div>
                    {side?.rateNarrative ? (
                      <div className='trq-option-narrative'>
                        {side.rateNarrative}
                      </div>
                    ) : null}
                    {/* The rate differs by line, and the narrative named
                        each one — so the user picks which describes their
                        goods rather than being locked out. */}
                    {variants.length > 0 ? (
                      <div className='trq-variants'>
                        <div className='trq-variants-prompt'>
                          Which of these are your goods?
                        </div>
                        {variants.map((v, i) => (
                          <span
                            key={`${v.label}-${i}`}
                            role='button'
                            tabIndex={disabled ? -1 : 0}
                            className={`trq-variant${
                              chosenIndex === i ? ' is-active' : ''
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!disabled) {
                                onChooseVariant?.(quota.iclItem || '', id, i);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!disabled) {
                                  onChooseVariant?.(quota.iclItem || '', id, i);
                                }
                              }
                            }}
                          >
                            <span className='trq-variant-rate'>
                              {v.rateDisplay}
                            </span>
                            <span className='trq-variant-label'>{v.label}</span>
                          </span>
                        ))}
                      </div>
                    ) : side?.rateVaries ? (
                      <div className='trq-option-varies'>
                        The rate differs across the tariff lines in this group,
                        so it cannot be calculated here — read the rate above
                        and confirm the figure for your line.
                      </div>
                    ) : null}
                  </button>
                );
              })}
            </div>

            {/* Only the declared side's permit requirement is shown: they
                differ materially (within access needs a GAC allocation, over
                access needs none) and showing both invites the wrong one. */}
            {declared && quota[declared]?.permitRequirement ? (
              <div className='trq-permit'>
                <span className='trq-permit-label'>Permit</span>
                {quota[declared]?.permitRequirement}
              </div>
            ) : null}

            {!declared ? (
              <div className='trq-quota-hint'>
                No duty is calculated until you declare — the two rates differ
                by orders of magnitude.
              </div>
            ) : null}

            {quota.missingSides.length > 0 ? (
              <div className='trq-quota-hint trq-quota-hint-warn'>
                The dataset carries no{' '}
                {quota.missingSides.join(' or ')}-access rate for this quota.
              </div>
            ) : null}
          </div>
        );
      })}

      {/* The card is a flex column, so a stretched button reads as another
          panel rather than a control. align-self keeps it to its content
          width, and the chevron says it opens something. */}
      <button
        type='button'
        className='btn-outline trq-details-btn'
        onClick={() => onViewDetails?.()}
      >
        Quota details
        <ChevronRightIcon sx={{ fontSize: 18 }} />
      </button>
    </div>
  );
};

export default CanadaTrqPanel;
